import { ProchainesInterventions } from '../../Composants/prochaines-interventions/prochaines-interventions';
import {
  creerModalIntervention,
  MaintenanceIntervention,
  formatTimeRange,
  trackIntervention,
  installationLabel,
  mapperIntervention,
  comparerInterventionsChronologiquement,
  couleursInterventions,
  lienTicket,
} from '../../Composants/prochaines-interventions/intervention-agenda';
import { InterventionActualisation } from '../../Services/intervention-actualisation';
import { InterventionAcces } from '../../Composants/intervention-acces/intervention-acces';
import {
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Authentification } from '../../Services/authentification';
import {
  peutConsulterInstallation,
  peutPlanifierInstallation,
} from '../../Services/droits-installations';
import { CategorieIntervention, NatureIntervention } from '../../Services/intervention-edition';
import { jourPlanification } from '../../Services/intervention-dates';
import { SpinnerOverlayComponent } from '../../Composants/spinner-overlay/spinner-overlay';
import { MaintenanceDayPanel, MaintenanceDayItem } from './maintenance-day-panel';
import {
  InterventionModalComponent,
  InterventionModalData,
} from '../../Composants/intervention-modal/intervention-modal';
import { EtatService } from '../../Services/etat';
import { formaterDateLongueAvecJour } from '../../Services/format';
import { ParcService } from '../../Services/parc';
import {
  FiltresInterventions,
  InterventionBrute,
  filtresInterventionsMois,
  reunirInterventions,
} from '../../Services/interventions';

type CalendarCell = {
  iso: string;
  day: number;
  isCurrentMonth: boolean;
  isToday: boolean;
  interventions: MaintenanceIntervention[];
};

@Component({
  selector: 'app-maintenance',
  imports: [
    ProchainesInterventions,
    InterventionModalComponent,
    SpinnerOverlayComponent,
    MaintenanceDayPanel,
    InterventionAcces,
    MatMenuModule,
  ],
  templateUrl: './maintenance.html',
  styleUrl: './maintenance.css',
})
export class Maintenance {
  private readonly actualisationInterventions = inject(InterventionActualisation);
  private readonly parcService = inject(ParcService);
  private readonly auth = inject(Authentification);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly accesIntervention = viewChild(InterventionAcces);
  private readonly menusJours = viewChildren(MatMenuTrigger);
  readonly jourCreation = signal<string | null>(null);
  readonly dateMinPlanification = signal(jourPlanification());
  readonly peutPlanifier = computed(
    () =>
      !!this.etat.parcSelectionne() &&
      this.etat
        .installations()
        .some((i) => peutPlanifierInstallation(this.auth.tokenInfoSignal(), i.id)),
  );
  readonly peutCreerBon = computed(
    () =>
      !!this.etat.parcSelectionne() &&
      this.etat
        .installations()
        .some((i) => peutConsulterInstallation(this.auth.tokenInfoSignal(), i.id)),
  );
  readonly peutCreerIntervention = computed(() => this.peutPlanifier() || this.peutCreerBon());
  private chargementId = 0;
  private destroyRef = inject(DestroyRef);
  readonly etat = inject(EtatService);
  readonly couleursInterventions = couleursInterventions;

  readonly today = new Date();
  readonly displayedMonth = signal(new Date(this.today.getFullYear(), this.today.getMonth(), 1));
  readonly modeVue = signal<'mois' | 'annee'>('mois');
  readonly periodeLabel = computed(() =>
    this.modeVue() === 'annee' ? String(this.displayedMonth().getFullYear()) : this.monthLabel(),
  );
  readonly periodeTexte = computed(() => (this.modeVue() === 'annee' ? 'cette année' : 'ce mois'));
  readonly chargementInterventions = signal(false);
  readonly erreurInterventions = signal('');

  readonly interventions = signal<MaintenanceIntervention[]>([]);
  readonly filtreType = signal<InterventionBrute['type'] | 'toutes'>('toutes');
  readonly filtresTypes = computed(() => [
    { value: 'toutes' as const, label: 'Toutes', count: this.interventions().length },
    {
      value: 'intervention_curative' as const,
      label: 'Curatives',
      count: this.interventions().filter((i) => i.type === 'intervention_curative').length,
    },
    {
      value: 'intervention_preventive' as const,
      label: 'Préventives',
      count: this.interventions().filter((i) => i.type === 'intervention_preventive').length,
    },
  ]);
  readonly interventionsAffichees = computed(() =>
    this.interventions().filter(
      (item) => this.filtreType() === 'toutes' || item.type === this.filtreType(),
    ),
  );
  readonly jourSelectionne = signal<string | null>(null);
  private boutonJournee: HTMLElement | null = null;
  readonly jourSelectionneLabel = computed(() => {
    const jour = this.jourSelectionne();
    return jour
      ? new Date(`${jour}T12:00:00`).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : '';
  });
  readonly interventionsJournee = computed<MaintenanceDayItem[]>(() =>
    this.interventionsAffichees()
      .filter((item) => item.startsAt.slice(0, 10) === this.jourSelectionne())
      .sort((a, b) => this.comparerInterventionsChronologiquement(a, b))
      .map((item) => ({
        key: this.trackIntervention(0, item),
        installation: this.installationLabel(item),
        intervenant: item.site,
        horaire: this.formatTimeRange(item),
        color: item.color,
      })),
  );
  readonly selectedInterventionId = signal<string | null>(null);
  readonly interventionOuverte = signal<MaintenanceIntervention | null>(null);
  readonly interventionModalData = computed<InterventionModalData | null>(() => {
    const intervention = this.interventionOuverte();
    if (!intervention) return null;

    return creerModalIntervention(intervention);
  });

  readonly weekdays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  readonly monthLabel = computed(() =>
    this.displayedMonth().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
  );

  readonly afficherProchainesInterventions = computed(() => {
    if (this.modeVue() === 'annee') return false;
    const moisAffiche = this.displayedMonth();
    const moisCourant = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    return moisAffiche.getTime() >= moisCourant.getTime();
  });

  readonly calendarCells = computed(() =>
    this.creerCellulesMois(this.displayedMonth(), this.interventionsAffichees()),
  );

  readonly moisAnnee = computed(() => {
    const annee = this.displayedMonth().getFullYear();
    const interventions = this.interventionsAffichees();
    return Array.from({ length: 12 }, (_, index) => {
      const date = new Date(annee, index, 1);
      const cleMois = this.toIsoDate(date).slice(0, 7);
      const items = interventions.filter((item) => item.startsAt.slice(0, 7) === cleMois);
      return {
        index,
        label: date.toLocaleDateString('fr-FR', { month: 'long' }),
        cellules: this.creerCellulesMois(date, items),
        nombre: items.length,
      };
    });
  });

  private creerCellulesMois(month: Date, items: MaintenanceIntervention[]): CalendarCell[] {
    const start = new Date(month.getFullYear(), month.getMonth(), 1);
    const startOffset = (start.getDay() + 6) % 7;
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - startOffset);

    const joursDuMois = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const nombreCellules = Math.ceil((startOffset + joursDuMois) / 7) * 7;
    return Array.from({ length: nombreCellules }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      const iso = this.toIsoDate(date);
      const interventions = items
        .filter((item) => item.startsAt.slice(0, 10) === iso)
        .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

      return {
        iso,
        day: date.getDate(),
        isCurrentMonth: date.getMonth() === month.getMonth(),
        isToday: iso === this.toIsoDate(this.today),
        interventions,
      };
    });
  }

  readonly prochainesInterventions = computed(() => {
    const aujourdHui = this.toIsoDate(new Date());

    return [...this.interventionsAffichees()]
      .filter((item) => item.startsAt.slice(0, 10) >= aujourdHui)
      .sort((a, b) => this.comparerInterventionsChronologiquement(a, b));
  });

  couleurJour(cell: CalendarCell): string {
    const preventive = cell.interventions.some((item) => item.type === 'intervention_preventive');
    const curative = cell.interventions.some((item) => item.type === 'intervention_curative');
    if (preventive && curative) {
      return `linear-gradient(135deg, ${this.couleursInterventions.intervention_preventive} 50%, ${this.couleursInterventions.intervention_curative} 50%)`;
    }
    if (preventive) return this.couleursInterventions.intervention_preventive;
    if (curative) return this.couleursInterventions.intervention_curative;
    return 'transparent';
  }

  libelleJour(cell: CalendarCell): string {
    return `${formaterDateLongueAvecJour(cell.iso)} : ${cell.interventions.length} intervention(s)${this.peutCreerIntervention() ? '. Créer une intervention' : ''}`;
  }

  /** Actualise la borne de date à chaque ouverture, même si la page a passé minuit. */
  preparerMenuJour(jour: string): void {
    this.dateMinPlanification.set(jourPlanification());
    this.jourCreation.set(jour);
  }

  /** Safari ne focalise pas toujours le bouton au clic ; le menu doit pouvoir y revenir. */
  focaliserJour(event: Event): void {
    if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus();
  }

  /** Réutilise le formulaire et ses droits ; le jour choisi reste une date civile YYYY-MM-DD. */
  creerDepuisJour(
    jour: string,
    nature: NatureIntervention,
    categorie: CategorieIntervention = 'curative',
  ): void {
    if (
      nature === 'planification'
        ? !this.peutPlanifier() || jour < jourPlanification()
        : !this.peutCreerBon()
    )
      return;
    const retourFocus = this.element.nativeElement.querySelector<HTMLElement>(
      `[data-jour-creation="${jour}"]`,
    );
    this.accesIntervention()?.ouvrir(nature, {
      ...(nature === 'planification' ? { preselection: { date: jour, categorie } } : {}),
      ...(retourFocus ? { retourFocus } : {}),
    });
  }

  private fermerMenuCreation(): void {
    for (const menu of this.menusJours()) if (menu.menuOpen) menu.closeMenu();
    this.jourCreation.set(null);
  }

  readonly selectedIntervention = computed(() => {
    const selectedId = this.selectedInterventionId();
    const items = this.interventionsAffichees();
    if (!items.length) return null;
    return items.find((item) => this.trackIntervention(0, item) === selectedId) ?? items[0];
  });

  changeMonth(offset: number) {
    const current = this.displayedMonth();
    this.fermerInterventionModal();
    this.displayedMonth.set(
      new Date(
        current.getFullYear(),
        current.getMonth() + offset * (this.modeVue() === 'annee' ? 12 : 1),
        1,
      ),
    );
  }

  changerModeVue(mode: 'mois' | 'annee') {
    this.fermerInterventionModal();
    this.modeVue.set(mode);
  }

  ouvrirMois(index: number) {
    this.fermerInterventionModal();
    this.displayedMonth.set(new Date(this.displayedMonth().getFullYear(), index, 1));
    this.modeVue.set('mois');
  }

  resetToday() {
    this.fermerInterventionModal();
    this.displayedMonth.set(new Date(this.today.getFullYear(), this.today.getMonth(), 1));
  }

  formatTimeRange(item: MaintenanceIntervention): string {
    return formatTimeRange(item);
  }

  trackCell(_: number, cell: CalendarCell) {
    return cell.iso;
  }

  trackIntervention(_: number, item: MaintenanceIntervention) {
    return trackIntervention(_, item);
  }

  selectIntervention(item: MaintenanceIntervention) {
    this.selectedInterventionId.set(this.trackIntervention(0, item));
  }

  changerFiltreType(type: InterventionBrute['type'] | 'toutes') {
    this.fermerJournee(false);
    this.filtreType.set(type);
    this.selectedInterventionId.set(null);
    this.fermerInterventionModal();
  }

  ouvrirInterventionModal(item: MaintenanceIntervention, event?: Event) {
    // Safari ne focalise pas le bouton au clic ; la consultation doit pouvoir lui rendre le focus.
    if (event?.currentTarget instanceof HTMLElement) event.currentTarget.focus();
    this.selectIntervention(item);
    this.interventionOuverte.set(item);
  }

  fermerInterventionModal() {
    this.interventionOuverte.set(null);
  }

  ouvrirJournee(cell: CalendarCell, event: Event) {
    this.boutonJournee = event.currentTarget as HTMLElement;
    this.jourSelectionne.set(cell.iso);
  }

  fermerJournee(restaurerFocus = true) {
    this.jourSelectionne.set(null);
    if (restaurerFocus) this.boutonJournee?.focus();
    this.boutonJournee = null;
  }

  ouvrirInterventionJournee(key: string) {
    const item = this.interventionsAffichees().find(
      (item) => this.trackIntervention(0, item) === key,
    );
    if (item) this.ouvrirInterventionModal(item);
  }

  installationLabel(item: MaintenanceIntervention): string {
    return installationLabel(item);
  }

  lienTicket(item: MaintenanceIntervention): string[] | null {
    return lienTicket(item);
  }

  isSelectedIntervention(item: MaintenanceIntervention): boolean {
    const selected = this.selectedIntervention();
    return selected?.id === item.id && selected.type === item.type;
  }

  constructor() {
    // Une sélection de jour ne doit pas survivre à un changement de parc, de vue ou de droits.
    effect(() => {
      this.etat.parcSelectionne();
      this.etat.installations();
      this.auth.tokenInfoSignal();
      this.displayedMonth();
      this.modeVue();
      untracked(() => this.fermerMenuCreation());
    });
    effect(() => {
      const parc = this.etat.parcSelectionne();
      this.actualisationInterventions.revision();
      const month = this.displayedMonth();
      const mode = this.modeVue();

      if (!parc) {
        this.fermerJournee(false);
        ++this.chargementId;
        this.interventions.set([]);
        this.chargementInterventions.set(false);
        this.fermerInterventionModal();
        return;
      }

      const filtres =
        mode === 'annee'
          ? { debut: `${month.getFullYear()}-01-01`, fin: `${month.getFullYear()}-12-31` }
          : filtresInterventionsMois(month);
      void this.chargerInterventionsParc(parc.id, filtres);
    });
  }

  private toIsoDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private async chargerInterventionsParc(parcId: number, filtres: FiltresInterventions) {
    const chargementId = ++this.chargementId;
    const mois = this.displayedMonth();
    const mode = this.modeVue();
    const estValide = () =>
      !this.destroyRef.destroyed &&
      chargementId === this.chargementId &&
      parcId === this.etat.parcSelectionne()?.id &&
      mois === this.displayedMonth() &&
      mode === this.modeVue();
    this.fermerJournee(false);
    this.fermerInterventionModal();
    this.interventions.set([]);
    this.chargementInterventions.set(true);
    this.erreurInterventions.set('');

    try {
      const response = await this.parcService.getParcInterventions(parcId, filtres);
      if (!estValide()) return;
      if (!response) throw new Error('Réponse interventions indisponible');
      const interventions = reunirInterventions(response)
        .map((item) => this.mapperIntervention(item))
        .sort((a, b) => this.comparerInterventionsChronologiquement(a, b));
      this.interventions.set(interventions);
      const prochaine = this.prochainesInterventions()[0];
      this.selectedInterventionId.set(prochaine ? this.trackIntervention(0, prochaine) : null);
    } catch {
      if (!estValide()) return;
      this.interventions.set([]);
      this.selectedInterventionId.set(null);
      this.erreurInterventions.set('Impossible de charger les interventions.');
    } finally {
      if (estValide()) this.chargementInterventions.set(false);
    }
  }

  private mapperIntervention(item: InterventionBrute): MaintenanceIntervention {
    return mapperIntervention(item);
  }

  private comparerInterventionsChronologiquement(
    a: MaintenanceIntervention,
    b: MaintenanceIntervention,
  ): number {
    return comparerInterventionsChronologiquement(a, b);
  }
}
